import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { env } from '@/lib/env';
import { isEmbeddedSignupConfigured, launchEmbeddedSignup } from '@/lib/meta-sdk';
import { useCompleteWhatsAppSignup } from './use-meta-oauth';

/**
 * Botón «Conectar con Facebook» de WhatsApp: abre el flujo de **Embedded Signup** (JS SDK de Meta)
 * para que el cliente elija su portfolio, su WABA y su número, y al terminar manda al backend el
 * `code` (vive 30 s), el `waba_id` y el `phone_number_id` (`POST .../meta/whatsapp/complete`).
 *
 * El backend hace el resto: canjea el code por el token del negocio, suscribe la WABA a los webhooks
 * del SaaS y guarda las credenciales cifradas. Si algo de eso falla sin impedir la conexión, se
 * muestran los `avisos` (por ejemplo, «no se pudo suscribir la WABA»).
 *
 * Si el despliegue no tiene `VITE_META_APP_ID` / `VITE_META_ES_CONFIG_ID`, en vez del botón se
 * explica qué falta y queda la carga manual de credenciales (el camino que ya existía) — pero **solo
 * si el canal todavía no está conectado**: con el canal conectado, ese aviso («la conexión todavía no
 * está configurada») contradice la insignia «Conectado» de la tarjeta y parece que algo se rompió
 * (bug visual real del panel, 20-sep-2026). En ese caso este componente no pinta nada.
 */
export interface WhatsAppSignupButtonProps {
  disabled?: boolean;
  /**
   * El canal ya está conectado (lo sabe la tarjeta, que lee el resumen del backend). Con
   * `conectado` y sin configuración de Embedded Signup, el componente no renderiza nada.
   */
  conectado?: boolean;
  /** Configuración explícita (pruebas); por defecto sale de `env`. */
  appId?: string;
  configId?: string;
}

export function WhatsAppSignupButton({
  disabled = false,
  conectado = false,
  appId = env.metaAppId,
  configId = env.metaEmbeddedSignupConfigId,
}: WhatsAppSignupButtonProps) {
  const { tenantId } = useActiveTenantAccess();
  const complete = useCompleteWhatsAppSignup(tenantId);

  const [error, setError] = useState<string | null>(null);
  const [avisos, setAvisos] = useState<string[]>([]);
  // Ojo con los nombres: esto es «se acaba de conectar en este flujo» (el éxito del Embedded Signup),
  // distinto del prop `conectado` (el canal YA estaba conectado según el backend).
  const [recienConectado, setRecienConectado] = useState(false);

  const configurado = isEmbeddedSignupConfigured(appId, configId);

  async function handleSignup(): Promise<void> {
    setError(null);
    setAvisos([]);
    setRecienConectado(false);

    try {
      const session = await launchEmbeddedSignup({ appId, configId });
      const resultado = await complete.mutateAsync({
        code: session.code,
        wabaId: session.wabaId,
        phoneNumberId: session.phoneNumberId,
      });
      setAvisos(resultado.avisos);
      setRecienConectado(true);
    } catch (caught) {
      setError(getErrorMessage(caught));
    }
  }

  if (!configurado) {
    // Canal ya conectado + Embedded Signup sin configurar: no hay nada que ofrecer y el aviso
    // confundiría (la tarjeta ya dice «Conectado» y tiene sus botones de reconectar/probar/desconectar).
    if (conectado) return null;

    return (
      <Alert tone="info">
        La conexión con Facebook para WhatsApp todavía no está configurada en este despliegue
        (faltan <code>VITE_META_APP_ID</code> y <code>VITE_META_ES_CONFIG_ID</code>): conecta el canal
        con las credenciales del negocio (<code>phone_number_id</code>, <code>waba_id</code>,{' '}
        <code>access_token</code> y <code>verify_token</code>).
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      <Button
        variant="secondary"
        leftIcon={<RefreshCw size={16} />}
        loading={complete.isPending}
        disabled={disabled}
        onClick={() => void handleSignup()}
      >
        Conectar con Facebook
      </Button>

      {error ? <Alert>{error}</Alert> : null}

      {recienConectado ? (
        <Alert tone={avisos.length > 0 ? 'warning' : 'success'}>
          {avisos.length > 0
            ? `Canal conectado, con avisos: ${avisos.join(' ')}`
            : 'Canal conectado: los mensajes entrantes del negocio ya llegan a este panel.'}
        </Alert>
      ) : null}
    </div>
  );
}
